import { jwtDecode } from "jwt-decode";
import {
  fetchAccessToken,
  fetchAccessTokenWithGoogle,
  resetPassword,
  signup,
} from "../services/auth";

const NETWORK_LATENCY_MS = 100;

type AccessToken = {
  accessToken: string;
  jwt: {
    email: string;
    id: number;
    exp: number;
    iat: number;
    iss: "qcsku.com";
  };
};

type SignUpData = {
  email: string;
  password: string;
  countryCode: string;
  firstName: string;
  lastName: string;
  slug: string;
};

export class AccessTokenManager {
  private controller = new AbortController();
  private token: AccessToken | null = null;

  public async signIn(email: string, password: string, persistent: boolean) {
    localStorage.removeItem("access_token");
    const { accessToken } = await fetchAccessToken(
      { email, password },
      this.controller.signal
    );
    await this.save(accessToken, persistent);
  }

  public async signInWithGoogle(
    googleAccessToken: string,
    persistent: boolean
  ) {
    localStorage.removeItem("access_token");
    const { accessToken } = await fetchAccessTokenWithGoogle({
      accessToken: googleAccessToken,
    });
    await this.save(accessToken, persistent);
    return true;
  }

  public async signUp(data: SignUpData) {
    const { accessToken } = await signup(data);
    await this.save(accessToken, true);
  }

  public async forgotPassword(email: string) {
    await resetPassword({ email });
  }

  private save(accessToken: string, persistent: boolean) {
    this.token = {
      accessToken,
      jwt: jwtDecode(accessToken),
    };
    if (persistent) {
      localStorage.setItem("access_token", accessToken);
    }
  }

  retrieve() {
    if (this.token === null) {
      const accessToken = localStorage.getItem("access_token");
      if (accessToken) {
        this.token = {
          accessToken,
          jwt: jwtDecode(accessToken),
        };
      }
    }

    if (
      this.token === null ||
      this.token.jwt.exp * 1000 < Date.now() + NETWORK_LATENCY_MS
    ) {
      window.location.href = "/auth/sign-in";
    }

    return this.token?.accessToken;
  }
}

export const accessTokenManager = new AccessTokenManager();
